import { logger as LOG } from '@garnish/logger';
import { assign } from 'xstate';

import { removeActiveInteractionState as removeActiveInteractionStateHelper } from '../../actions';
import { removeTokens, storeHybridFlowTokens } from '../../helpers';
import {
  type AccessTokens,
  type AccountData,
  type AzureAuthMachineAction,
  type AzureAuthStateMachineContext,
} from '../azure-auth-machine.types';

// ─── External Actions ────────────────────────────────────────────────────────

const onSystemError: AzureAuthMachineAction<{ systemError: unknown }> = () => {
  logger.debug('`onSystemError` action is not provided.');
};

// ─── Internal Actions ────────────────────────────────────────────────────────

const storeTokens: AzureAuthMachineAction = ({ context }) => {
  const { apiRefreshToken, apiAccessToken } = context;

  if (!apiRefreshToken || !apiAccessToken) return;

  void storeHybridFlowTokens({
    accessToken: apiAccessToken,
    refreshToken: apiRefreshToken,
  });
};

const removeRefreshToken: AzureAuthMachineAction = () => {
  void removeTokens();
};

const storeAccountData: AzureAuthMachineAction<{ accountData: AccountData }> = assign(
  (_args, params) => {
    const { accountData } = params;

    if (!accountData) return {};

    const { email, apiAccessToken, apiRefreshToken } = accountData;

    return { email, apiAccessToken, apiRefreshToken };
  },
);

const storeAccessTokens: AzureAuthMachineAction<{ accessTokens: AccessTokens }> = assign(
  (_args, params) => {
    const { accessTokens } = params;

    if (!accessTokens) return {};

    const { apiAccessToken } = accessTokens;

    return { apiAccessToken };
  },
);

const storeAccountInput: AzureAuthMachineAction<
  Pick<AzureAuthStateMachineContext, 'createAccountInput'>
> = assign((_args, params) => ({
  createAccountInput: params.createAccountInput,
}));

const removeActiveInteractionState: AzureAuthMachineAction = () => {
  removeActiveInteractionStateHelper();
};

// ─────────────────────────────────────────────────────────────────────────────

export const Actions = {
  onSystemError,
  storeTokens,
  removeRefreshToken,
  storeAccountData,
  storeAccessTokens,
  storeAccountInput,
  removeActiveInteractionState,
};

// ─── Logger ──────────────────────────────────────────────────────────────────

const logger = LOG.extend('AZURE AUTH STATE MACHINE');
